import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Headset, Send, X } from 'lucide-react';
import { UserRole, WS_EVENT, type SupportMessagePayload } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useAuth } from '@/lib/auth-store';
import { sonarAviso } from '@/lib/sonido';
import { useEventoSocket } from '@/lib/socket';
import { Boton } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface MensajeSoporte {
  id: string;
  senderId: string;
  senderRole: UserRole;
  body: string;
  readAt: string | null;
  createdAt: string;
}

/**
 * Soporte, flotando siempre.
 *
 * El botón está en todas las pantallas de la tienda porque el momento en que
 * alguien necesita ayuda no se puede predecir: puede ser mirando el menú, en
 * mitad del checkout o esperando el pedido. Obligar a buscar una sección de
 * ayuda es perder a quien iba a preguntar.
 *
 * Sin sesión el botón sigue visible pero no abre el chat: invita a entrar.
 * Ocultarlo dejaría a quien no tiene cuenta sin saber que existe soporte.
 */
export function BotonSoporte() {
  const accessToken = useAuth((e) => e.accessToken);
  const user = useAuth((e) => e.user);
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const final = useRef<HTMLDivElement>(null);

  const conSesion = !!accessToken && user?.role === UserRole.CUSTOMER;

  const sinLeer = useQuery({
    queryKey: ['soporte', 'sin-leer'],
    queryFn: async () =>
      (await api.get<{ total: number }>('/soporte/chat/sin-leer')).data.total,
    enabled: conSesion,
    refetchInterval: conSesion ? 120_000 : false,
  });

  const mensajes = useQuery({
    queryKey: ['soporte', 'mensajes'],
    queryFn: async () => (await api.get<MensajeSoporte[]>('/soporte/chat')).data,
    enabled: conSesion && abierto,
  });

  const enviar = useMutation({
    mutationFn: async (body: string) =>
      (await api.post<MensajeSoporte>('/soporte/chat', { body })).data,
    onSuccess: () => {
      setTexto('');
      void cliente.invalidateQueries({ queryKey: ['soporte'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  useEventoSocket<SupportMessagePayload>(WS_EVENT.SUPPORT_MESSAGE, (mensaje) => {
    void cliente.invalidateQueries({ queryKey: ['soporte'] });
    // Suena solo lo que escribe administración: el propio mensaje no timbra.
    if (mensaje.senderRole === UserRole.ADMIN) sonarAviso();
  });

  useEffect(() => {
    if (abierto) final.current?.scrollIntoView({ block: 'end' });
  }, [abierto, mensajes.data]);

  /**
   * Abrir el chat cuenta como leerlo.
   *
   * Pedir la conversación ya la marca leída en el servidor; lo que faltaba era
   * que el contador del botón se enterara, porque solo se refrescaba cada dos
   * minutos. El globo rojo seguía encima del mensaje que se estaba leyendo.
   */
  useEffect(() => {
    if (!mensajes.data) return;
    void cliente.invalidateQueries({ queryKey: ['soporte', 'sin-leer'] });
  }, [mensajes.data, cliente]);

  const pendientes = sinLeer.data ?? 0;
  const lista = mensajes.data ?? [];

  return (
    <>
      {/*
        Sube por encima de la barra inferior en móvil, donde está el carrito.
        Un botón flotante que tapa el botón de pagar es peor que no tenerlo.
      */}
      <button
        onClick={() => setAbierto((v) => !v)}
        aria-label={
          pendientes > 0 ? `Soporte, ${pendientes} sin leer` : 'Soporte'
        }
        className="fixed bottom-20 right-4 z-40 grid h-14 w-14 place-items-center rounded-full bg-kick-dark text-white shadow-lg transition hover:scale-105 active:scale-95 sm:bottom-6"
      >
        {abierto ? <X className="h-6 w-6" /> : <Headset className="h-6 w-6" />}
        {!abierto && pendientes > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-kick-red px-1 text-[11px] font-bold text-white">
            {pendientes > 9 ? '9+' : pendientes}
          </span>
        )}
      </button>

      {abierto && (
        <div className="fixed bottom-36 right-4 z-40 flex h-[min(28rem,70vh)] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-kick bg-white shadow-2xl sm:bottom-24">
          <header className="bg-kick-dark px-4 py-3">
            <p className="font-bold text-white">Soporte de Kick</p>
            <p className="text-xs text-white/60">
              {conSesion ? 'Te respondemos lo antes posible' : 'Entra para escribirnos'}
            </p>
          </header>

          {!conSesion ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
              <Headset className="h-10 w-10 text-kick-gray/40" />
              <p className="text-sm text-kick-gray">
                Necesitamos saber quién eres para poder ayudarte con tus pedidos.
              </p>
              <Link to="/acceso" onClick={() => setAbierto(false)}>
                <Boton>Entrar a mi cuenta</Boton>
              </Link>
            </div>
          ) : (
            <>
              <div className="flex-1 space-y-2 overflow-y-auto bg-kick-light p-4">
                {mensajes.isLoading && (
                  <p className="text-center text-sm text-kick-gray">Cargando…</p>
                )}

                {!mensajes.isLoading && lista.length === 0 && (
                  <div className="px-2 py-6 text-center">
                    <p className="text-sm text-kick-gray">
                      Cuéntanos qué necesitas. Si es por un pedido concreto, dinos el código
                      —empieza por KD— y lo miramos de una.
                    </p>
                  </div>
                )}

                {lista.map((m) => {
                  const mio = m.senderRole === UserRole.CUSTOMER;

                  return (
                    <div key={m.id} className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
                      <div
                        className={cn(
                          'max-w-[85%] rounded-kick px-3 py-2 text-sm',
                          mio ? 'bg-kick-red text-white' : 'bg-white text-kick-ink',
                        )}
                      >
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p
                          className={cn(
                            'mt-0.5 text-[11px]',
                            mio ? 'text-white/70' : 'text-kick-gray',
                          )}
                        >
                          {new Date(m.createdAt).toLocaleTimeString('es-CO', {
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Bogota',
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}

                <div ref={final} />
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (texto.trim()) enviar.mutate(texto.trim());
                }}
                className="flex gap-2 border-t border-kick-gray/15 p-3"
              >
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  maxLength={1000}
                  placeholder="Escribe tu mensaje…"
                  aria-label="Mensaje para soporte"
                  className="min-w-0 flex-1 rounded-kick border border-kick-gray/30 px-3 py-2.5 text-sm text-kick-ink outline-none transition focus:border-kick-red"
                />
                <Boton
                  type="submit"
                  className="px-3"
                  disabled={!texto.trim()}
                  cargando={enviar.isPending}
                  icono={<Send className="h-4 w-4" />}
                >
                  <span className="sr-only">Enviar</span>
                </Boton>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
